import { useEffect, useRef, useCallback } from 'react';
import { useChatStore, type Message } from '@/stores/chatStore';
import { useUIStore } from '@/stores/uiStore';
import { useChat } from '@/hooks/useChat';
import { useVoice } from '@/hooks/useVoice';
import HoloMessage from './HoloMessage';
import GlassInput from './GlassInput';
import JarvisGreeting from '@/components/JarvisGreeting';
import ArcReactor from '@/components/ArcReactor';

function EmptyState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 boot-2">
      <div className="text-center w-full max-w-xl">
        {/* Arc Reactor */}
        <div className="relative inline-block">
          <ArcReactor size={220} />
        </div>

        {/* Hard gap between rings and greeting — responsive: scales with
            viewport height so tall screens get more breathing room. */}
        <div aria-hidden style={{ height: 'clamp(20px, 5vh, 56px)' }} />

        <div className="mb-6 w-full">
          <JarvisGreeting variant="floating" fallbackGreeting="SYSTEMS ONLINE" />
        </div>

        <div className="flex items-center justify-center gap-5">
          <div className="flex flex-col items-center gap-1">
            <div className="status-dot online" style={{ width: 5, height: 5 }} />
            <span className="text-[8px] font-mono text-gray-600 tracking-wider">CORE</span>
          </div>
          <div className="w-px h-4 bg-white/[0.05]" />
          <div className="flex flex-col items-center gap-1">
            <div className="status-dot online" style={{ width: 5, height: 5 }} />
            <span className="text-[8px] font-mono text-gray-600 tracking-wider">UPLINK</span>
          </div>
          <div className="w-px h-4 bg-white/[0.05]" />
          <div className="flex flex-col items-center gap-1">
            <div className="status-dot online" style={{ width: 5, height: 5 }} />
            <span className="text-[8px] font-mono text-gray-600 tracking-wider">VOICE</span>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-0 max-w-[240px] mx-auto">
          {/* Left ticks */}
          <div className="flex items-center gap-[3px] mr-1">
            <div className="w-[2px] h-[3px] bg-jarvis-blue/15 rounded-full" />
            <div className="w-[2px] h-[5px] bg-jarvis-blue/20 rounded-full" />
          </div>
          {/* Main divider */}
          <div className="hud-divider flex-1">
            <div className="hud-divider-dot" />
          </div>
          {/* Right ticks */}
          <div className="flex items-center gap-[3px] ml-1">
            <div className="w-[2px] h-[5px] bg-jarvis-blue/20 rounded-full" />
            <div className="w-[2px] h-[3px] bg-jarvis-blue/15 rounded-full" />
          </div>
        </div>

        <p className="mt-4 text-[9px] text-gray-600 font-mono tracking-wider">
          TYPE OR SPEAK TO BEGIN
        </p>
      </div>
    </div>
  );
}

export default function FloatingChat() {
  const { messages, currentConversation, sendMessage, isStreaming, createConversation } = useChat();
  const isThinking = useUIStore((s) => s.isThinking);
  const { isRecording, isTranscribing, startRecording, stopRecording, transcribeAudio } =
    useVoice();

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // CMD+N new session handler
  useEffect(() => {
    const handler = () => { createConversation(); };
    window.addEventListener('jarvis-new-session', handler);
    return () => window.removeEventListener('jarvis-new-session', handler);
  }, [createConversation]);

  const handleVoiceToggle = useCallback(async () => {
    if (isRecording) {
      const audioBlob = await stopRecording();
      if (audioBlob) {
        const text = await transcribeAudio(audioBlob);
        if (text && text.trim()) {
          sendMessage(text);
        }
      }
    } else {
      startRecording();
    }
  }, [isRecording, stopRecording, startRecording, transcribeAudio, sendMessage]);

  const hasMessages = messages.length > 0;

  // Group consecutive error messages
  const displayMessages = messages.reduce<
    (Message | { type: 'error_stack'; errors: Message[] })[]
  >((acc, msg) => {
    if (msg.role === 'system' && msg.id.startsWith('error-')) {
      const last = acc[acc.length - 1];
      if (last && 'type' in last && last.type === 'error_stack') {
        last.errors.push(msg);
      } else {
        acc.push({ type: 'error_stack', errors: [msg] });
      }
    } else {
      acc.push(msg);
    }
    return acc;
  }, []);

  // Derive thinking state from chat data
  const lastMessage = messages[messages.length - 1];
  const awaitingResponse =
    !!lastMessage && lastMessage.role === 'user' && lastMessage.id.startsWith('user-');
  const showThinking = awaitingResponse && !isStreaming;

  return (
    <div className="fixed inset-x-0 top-[72px] bottom-[24px] z-20 flex flex-col items-center pointer-events-none">
      {/* Messages area */}
      {hasMessages || showThinking ? (
        <div className="w-full max-w-4xl flex-1 overflow-y-auto px-5 sm:px-8 py-6 pointer-events-auto chat-scroll-mask">
          {displayMessages.map((item) => {
            if ('type' in item && item.type === 'error_stack') {
              const count = item.errors.length;
              const latestError = item.errors[item.errors.length - 1];
              return (
                <div key={latestError.id} className="flex justify-center my-3">
                  <div className="glass-subtle hud-clip-sm px-4 py-2 max-w-lg border border-hud-red/10">
                    <p className="text-[10px] text-center text-red-400 font-mono">
                      {count > 1
                        ? `${latestError.content} (+${count - 1} more)`
                        : latestError.content}
                    </p>
                  </div>
                </div>
              );
            }
            return <HoloMessage key={(item as Message).id} message={item as Message} />;
          })}

          {/* Thinking indicator */}
          {showThinking && (
            <HoloMessage
              message={{
                id: 'thinking-placeholder',
                role: 'assistant',
                content: '',
                timestamp: new Date().toISOString(),
                isStreaming: true,
              }}
            />
          )}
          <div ref={messagesEndRef} />
        </div>
      ) : (
        <EmptyState />
      )}

      {/* Input bar */}
      <div className="w-full max-w-4xl px-5 sm:px-8 pt-2 pb-2 pointer-events-auto boot-3">
        <GlassInput
          onSend={sendMessage}
          onVoiceToggle={handleVoiceToggle}
          isLoading={isStreaming || isThinking}
          isRecording={isRecording}
          disabled={isTranscribing}
        />
      </div>
    </div>
  );
}
